/**
 * Image field with upload, for the Directus file-reference columns.
 *
 * NETWORK BOUNDARY — read before changing this.
 *
 * Entity data goes to Postgres; file bytes cannot, so they go to a Supabase
 * storage bucket instead. Images could never have lived in localStorage
 * anyway -- a handful of photos exhausts the ~5MB quota, and object URLs die
 * on reload.
 *
 * What crosses the boundary is deliberately narrow:
 *   - ONLY file bytes are uploaded. No entity data, no store, no history.
 *   - What lands back in the store is a plain public URL string, exactly the
 *     shape these fields already held.
 *   - Nothing is ever read back from Supabase to populate the store.
 *
 * In Directus these columns hold a file id into directus_files; here they hold
 * a URL. Both are opaque strings to everything downstream, which is why the
 * field still accepts a pasted value — an existing Directus id or an external
 * URL stays editable by hand.
 */

import { MediaLibraryButton } from "./MediaLibrary"
import { useCallback, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog"
import {
  uploadImage, isOwnBucketUrl, SMART_CMS_BUCKET, IMAGE_ACCEPT,
} from "../uploadImage"
import { Upload, X, Loader2, ImageOff, Maximize2, ExternalLink } from "lucide-react"


export interface ImageFieldProps {
  label: string
  /** Current value: a public URL, a legacy Directus file id, or empty. */
  value?: string
  /**
   * Source-locale image shown when this locale has none of its own. Displayed
   * dimmed and labelled as inherited: like the text fields' placeholder, it is
   * NOT this locale's value, so it is never written back on edit.
   */
  fallbackValue?: string
  onChange: (value: string) => void
  hint?: string
}

export function ImageField({ label, value, fallbackValue, onChange, hint }: ImageFieldProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const upload = useCallback(async (file: File) => {
    setError(null)
    setUploading(true)
    const result = await uploadImage(file)
    setUploading(false)
    // Clear the input so re-picking the same file fires onChange again.
    if (inputRef.current) inputRef.current.value = ""
    if (result.ok && result.url) {
      setPreviewFailed(false)
      onChange(result.url)
    } else {
      setError(result.error ?? "Upload failed.")
    }
  }, [onChange])

  const clear = () => {
    setError(null)
    setPreviewFailed(false)
    onChange("")
    // The stored URL is dropped, but the object stays in the bucket. Deleting
    // it here would break any other block still pointing at the same file, and
    // this sandbox has no reference counting to know.
  }

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>

      {value && (
        <div className="flex items-start gap-2 rounded border p-2">
          {previewFailed ? (
            <div
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground"
              title="This value is not a loadable image URL"
            >
              <ImageOff className="h-5 w-5" />
            </div>
          ) : (
            // A thumbnail is too small to judge a real image by, so clicking it
            // opens the full-size version rather than making the user open the
            // URL in a new tab to see what they picked.
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              title="Click to preview full size"
              aria-label="Preview image full size"
              className="group relative h-16 w-16 shrink-0 overflow-hidden rounded border"
            >
              <img
                src={value}
                alt=""
                className="h-full w-full object-cover"
                onError={() => setPreviewFailed(true)}
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100">
                <Maximize2 className="h-4 w-4" />
              </span>
            </button>
          )}
          <div className="min-w-0 flex-1 space-y-1">
            <p className="break-all text-[11px] text-muted-foreground">{value}</p>
            {previewFailed && (
              <p className="text-[11px] text-amber-600">
                {isOwnBucketUrl(value)
                  // A URL we just wrote that will not load back is almost always
                  // a private bucket: the upload is permitted but the public
                  // read endpoint refuses with a 400. Say so, rather than
                  // leaving the user to guess.
                  ? <>Uploaded, but this URL will not load. The <code>{SMART_CMS_BUCKET}</code> bucket is most likely not marked public — turn on “Public bucket” for it in Supabase Storage.</>
                  : "Not loadable as an image. If this is a legacy Directus file id rather than a URL, that is expected."}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={clear}
            title="Remove this image"
            aria-label="Remove this image"
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {!value && fallbackValue && (
        <div className="flex items-center gap-2 rounded border border-dashed p-2">
          <img
            src={fallbackValue}
            alt=""
            className="h-12 w-12 shrink-0 rounded border object-cover opacity-60"
          />
          <p className="text-[11px] text-muted-foreground">
            Using the source image. Upload one here to override it for this locale.
          </p>
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={e => {
            const f = e.target.files?.[0]
            if (f) upload(f)
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 text-xs"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading
            ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Uploading…</>
            : <><Upload className="mr-1 h-3.5 w-3.5" /> {value ? "Replace" : "Upload"}</>}
        </Button>
        <MediaLibraryButton onPick={url => { setError(null); setPreviewFailed(false); onChange(url) }} />
        {/* Still hand-editable: a Directus file id or an external URL is valid. */}
        <Input
          value={value ?? ""}
          onChange={e => { setPreviewFailed(false); onChange(e.target.value) }}
          placeholder="…or paste a URL / file id"
          className="h-8 text-sm"
        />
      </div>

      {error && <p className="text-[11px] text-destructive">{error}</p>}
      {hint && !error && <p className="text-[11px] text-muted-foreground">{hint}</p>}

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-sm">{label}</DialogTitle>
          </DialogHeader>
          {/*
            max-h keeps a tall image inside the viewport, and object-contain
            shows the whole thing rather than cropping it the way the
            thumbnail's object-cover does — the point of opening this is to
            see the real framing.
          */}
          <img
            src={value}
            alt=""
            className="max-h-[70vh] w-full rounded object-contain"
            onError={() => { setPreviewFailed(true); setLightboxOpen(false) }}
          />
          <p className="break-all text-[11px] text-muted-foreground">{value}</p>
          <DialogFooter>
            {/*
              base-ui's Button renders through `render` rather than Radix's
              `asChild`, so the anchor is passed as the rendered element.
            */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              render={<a href={value} target="_blank" rel="noopener noreferrer" />}
            >
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open original
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
